import type { CountSpecMisc, WidgetStartDateType } from "../../Processing/types/builderMisc";
import type {
  PieChartComponentSpec,
  PieSeriesDef,
  PieSortOption,
  MetricOption,
  SubjectEntity,
} from "../../Processing/types";
import { DATE_RANGE_DEFAULTS } from "../shared/DateRangeForm";
import { PLAYERS_DEFAULTS, resolveSubjectNames } from "../shared/PlayersForm";

// ── Per-slice state ───────────────────────────────────────────

export interface PieSliceState {
  /** UUID of the Slicer this section follows, when it is bound. */
  dateControllerId?: string;
  subjectControllerId?: string;
  subjectControllerMode?: "positions";
  queryId: string;
  selectedMetricId: string | null;
  /** Which items a count metric tallies. Absent for every measured metric. */
  countSpec?: CountSpecMisc;
  metricAgg: "avg" | "sum" | "best";
  selectedSubjects: string[];
  selectedDrills: string[];
  subjectActiveTab: "athletes" | "drills";
  labelOverrideEnabled: boolean;
  labelOverride: string;
  // per-slice date range
  dateRange: string;
  showAdvancedDate: boolean;
  startDateType: WidgetStartDateType;
  startDateLatestN?: number;
  startDateAbsolute: string;
  startDateOffset: number;
  endDateType: "relative" | "absolute";
  endDateAbsolute: string;
  endDateOffset: number;
  dayFilter: string[];
  excludeInjuredPlayers: boolean;
  // inherit from slice 0 (only applies at index > 0)
  inheritDateRange: boolean;
  // ACWR — only valid when selectedMetricId starts with "gps-"
  showAcwr: boolean;
  // color override
  enableColorOverride: boolean;
  colorOverride: string;
}

// ── Form-level state ──────────────────────────────────────────

export interface PieFormState {
  slices: PieSliceState[];
  expandedSliceKey: string | undefined;
  // display
  title: string;
  showLegend: boolean;
  donutMode: boolean;
  sortBy: PieSortOption | null;
  showDataLabels: boolean;
  showPercentages: boolean;
}

// ── Actions ───────────────────────────────────────────────────

export type PieAction =
  | { type: "ADD_SLICE" }
  | { type: "REMOVE_SLICE"; index: number }
  | { type: "PATCH_SLICE"; index: number; patch: Partial<PieSliceState> }
  | { type: "SET_EXPANDED_SLICE_KEY"; key: string | undefined }
  | { type: "SET_TITLE"; title: string }
  | { type: "SET_SHOW_LEGEND"; value: boolean }
  | { type: "SET_DONUT_MODE"; value: boolean }
  | { type: "SET_SORT_BY"; sortBy: PieSortOption | null }
  | { type: "SET_SHOW_DATA_LABELS"; value: boolean }
  | { type: "SET_SHOW_PERCENTAGES"; value: boolean };

// ── Reducer ───────────────────────────────────────────────────

export function pieReducer(state: PieFormState, action: PieAction): PieFormState {
  switch (action.type) {
    case "ADD_SLICE": {
      const newSlice = createSlice(false);
      return {
        ...state,
        slices: [...state.slices, newSlice],
        expandedSliceKey: `slice-${newSlice.queryId}`,
      };
    }
    case "REMOVE_SLICE": {
      const slices = state.slices.filter((_, i) => i !== action.index);
      const removedKey = `slice-${state.slices[action.index]?.queryId}`;
      const expandedSliceKey =
        state.expandedSliceKey === removedKey
          ? slices[0] ? `slice-${slices[0].queryId}` : undefined
          : state.expandedSliceKey;
      return { ...state, slices, expandedSliceKey };
    }
    case "PATCH_SLICE": {
      const slices = state.slices.map((s, i) =>
        i === action.index ? { ...s, ...action.patch } : s,
      );
      return { ...state, slices };
    }
    case "SET_EXPANDED_SLICE_KEY": return { ...state, expandedSliceKey: action.key };
    case "SET_TITLE":            return { ...state, title: action.title };
    case "SET_SHOW_LEGEND":      return { ...state, showLegend: action.value };
    case "SET_DONUT_MODE":       return { ...state, donutMode: action.value };
    case "SET_SORT_BY":          return { ...state, sortBy: action.sortBy };
    case "SET_SHOW_DATA_LABELS": return { ...state, showDataLabels: action.value };
    case "SET_SHOW_PERCENTAGES": return { ...state, showPercentages: action.value };
    default:                     return state;
  }
}

// ── Helpers ───────────────────────────────────────────────────

function createSlice(isFirst = true): PieSliceState {
  return {
    queryId: crypto.randomUUID(),
    selectedMetricId: null,
    countSpec: undefined,
    metricAgg: "avg",
    ...PLAYERS_DEFAULTS,
    labelOverrideEnabled: false,
    labelOverride: "",
    ...DATE_RANGE_DEFAULTS,
    excludeInjuredPlayers: true,
    inheritDateRange: !isFirst,
    showAcwr: false,
    enableColorOverride: false,
    colorOverride: "",
  };
}

// ── Lifecycle functions ───────────────────────────────────────

export function createInitialState(firstMetricId?: string): PieFormState {
  const firstSlice = createSlice(true);
  if (firstMetricId) firstSlice.selectedMetricId = firstMetricId;
  return {
    slices: [firstSlice],
    expandedSliceKey: `slice-${firstSlice.queryId}`,
    title: "",
    showLegend: true,
    donutMode: false,
    sortBy: "value-desc",
    showDataLabels: false,
    showPercentages: false,
  };
}

export function hydrateFromSpec(spec: PieChartComponentSpec): PieFormState {
  const slices: PieSliceState[] = spec.series.map((s, idx) => {
    const misc = s.series_builder_misc;

    return {
      queryId: s.queryId,
      selectedMetricId: misc.selectedMetricId ?? null,
      countSpec: misc.countSpec,
      metricAgg: s.metricAgg,
      selectedSubjects: misc.selectedSubjects ?? PLAYERS_DEFAULTS.selectedSubjects,
      selectedDrills: misc.selectedDrills ?? PLAYERS_DEFAULTS.selectedDrills,
      subjectActiveTab: misc.subjectActiveTab ?? PLAYERS_DEFAULTS.subjectActiveTab,
      labelOverrideEnabled: !!(misc.labelOverride?.trim()),
      labelOverride: misc.labelOverride ?? "",
      dateRange: misc.dateRangePreset ?? DATE_RANGE_DEFAULTS.dateRange,
      showAdvancedDate: misc.showAdvancedDate ?? DATE_RANGE_DEFAULTS.showAdvancedDate,
      startDateType: misc.startDateType ?? DATE_RANGE_DEFAULTS.startDateType,
      startDateLatestN: misc.startDateLatestN,
      startDateAbsolute: misc.startDateAbsolute ?? DATE_RANGE_DEFAULTS.startDateAbsolute,
      startDateOffset: misc.startDateOffset ?? DATE_RANGE_DEFAULTS.startDateOffset,
      endDateType: misc.endDateType ?? DATE_RANGE_DEFAULTS.endDateType,
      endDateAbsolute: misc.endDateAbsolute ?? DATE_RANGE_DEFAULTS.endDateAbsolute,
      endDateOffset: misc.endDateOffset ?? DATE_RANGE_DEFAULTS.endDateOffset,
      dayFilter: misc.dayFilter ?? DATE_RANGE_DEFAULTS.dayFilter,
      dateControllerId: misc.dateControllerId,
      subjectControllerId: misc.subjectControllerId,
      subjectControllerMode: misc.subjectControllerMode,
      excludeInjuredPlayers: misc.excludeInjuredPlayers ?? true,
      inheritDateRange: idx > 0 && (misc.inheritDateRange ?? false),
      showAcwr: s.metricIdentifierType === "acwr",
      enableColorOverride: !!s.colorOverride,
      colorOverride: s.colorOverride ?? "",
    };
  });

  const hydratedSlices = slices.length > 0 ? slices : [createSlice(true)];
  return {
    slices: hydratedSlices,
    expandedSliceKey: `slice-${hydratedSlices[0].queryId}`,
    title: spec.display.title ?? "",
    // Legend is always enabled; the toggle was removed but the schema field stays.
    showLegend: true,
    donutMode: spec.widgetMutations.donutMode ?? false,
    sortBy: spec.widgetMutations.sortConfig?.enabled
      ? (spec.widgetMutations.sortConfig.sortBy ?? "value-desc")
      : null,
    showDataLabels: spec.series[0]?.plotMutations.showDataLabels ?? false,
    showPercentages: spec.series[0]?.plotMutations.showPercentages ?? false,
  };
}

export function formStateToSpec(
  state: PieFormState,
  metrics: MetricOption[],
  getSubjectEntities?: (subjectType: string) => SubjectEntity[],
): PieChartComponentSpec | null {
  const validSlices = state.slices.filter((s) => s.selectedMetricId != null);
  if (validSlices.length === 0) return null;

  const firstSlice = validSlices[0];
  const firstMetricName = metrics.find((m) => m.id === firstSlice.selectedMetricId)?.name ?? null;

  const series: PieSeriesDef[] = validSlices.map((slice, idx) => {
    const metric = metrics.find((m) => m.id === slice.selectedMetricId);

    // Resolve inherited date range from the first valid slice
    const dr = idx > 0 && slice.inheritDateRange ? firstSlice : slice;

    const selectedSubjectNames = getSubjectEntities
      ? resolveSubjectNames(slice.selectedSubjects, slice.selectedDrills, getSubjectEntities)
      : [];

    const isGps = slice.selectedMetricId?.startsWith("gps-") ?? false;
    return {
      queryId: slice.queryId,
      displayName: metric?.name ?? "Value",
      displayNameOverride: slice.labelOverride?.trim() || undefined,
      metricIdentifierType: slice.countSpec ? "count" : (isGps && slice.showAcwr ? "acwr" : "metric"),
      metricAgg: slice.metricAgg,
      colorOverride: slice.enableColorOverride && slice.colorOverride ? slice.colorOverride : undefined,
      plotMutations: {
        showDataLabels: state.showDataLabels,
        showPercentages: state.showPercentages,
      },
      series_builder_misc: {
        selectedMetricId: slice.selectedMetricId,
        countSpec: slice.countSpec,
        dateRangePreset: dr.dateRange,
        showAdvancedDate: dr.showAdvancedDate,
        startDateType: dr.startDateType,
        startDateLatestN: dr.startDateLatestN,
        startDateAbsolute: dr.startDateAbsolute,
        startDateOffset: dr.startDateOffset,
        endDateType: dr.endDateType,
        endDateAbsolute: dr.endDateAbsolute,
        endDateOffset: dr.endDateOffset,
        dayFilter: dr.dayFilter,
        dateControllerId: dr.dateControllerId,
        subjectControllerId: slice.subjectControllerId,
        subjectControllerMode: slice.subjectControllerMode,
        selectedSubjects: slice.selectedSubjects,
        selectedDrills: slice.selectedDrills,
        subjectActiveTab: slice.subjectActiveTab,
        labelOverride: slice.labelOverride || undefined,
        excludeInjuredPlayers: slice.excludeInjuredPlayers,
        inheritDateRange: idx > 0 ? slice.inheritDateRange : undefined,
        showAcwr: isGps && slice.showAcwr ? true : undefined,
        selectedSubjectNames,
      },
    };
  });

  return {
    type: "pie_chart",
    display: {
      title: state.title || firstMetricName || null,
      showLegend: state.showLegend,
    },
    widgetMutations: {
      donutMode: state.donutMode,
      sortConfig: state.sortBy != null
        ? { enabled: true, sortBy: state.sortBy }
        : { enabled: false, sortBy: "value-desc" },
    },
    series,
    pie_builder_misc: {},
  };
}
